import {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Button,
    TextField,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    Box,
    CircularProgress,
    Stack,
    Autocomplete,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingChangesService from "../service/PendingChangesService";
import {DateUtil, ServiceRequestFormat} from "base/utilities";
import {AlertError, PaperContainer, TableDownload} from "base/component/presentational";
import {PageLayout} from "base/layout";
import {EntityTableSortingHead} from "base/entity";
import {ConcelloService} from "concello/service";
import {AglomeracionService} from "saneamiento/aglomeracion/service";
import {useNavigate} from "react-router-dom";

import useListSearchParams, {textParam, choiceParam, pageParam, dateParam} from "base/hooks/useListSearchParams";

const querySchema = {
    page: pageParam,
    sort: textParam("action_date"),
    order: choiceParam("desc", ["asc", "desc"]),
    entity: textParam(),
    user: textParam(),
    date_from: dateParam,
    date_to: dateParam,
    state: choiceParam("PENDING", ["PENDING", "APPROVED", "REJECTED", "ALL"]),
    concello: textParam(),
    aglomeracion: textParam(),
};

const pageSize = 20;

const entityPathPluralMap = {
    captacion: "captacions",
    deposito: "depositos",
    etap: "etaps",
    sistema: "sistemas",
    bombeo: "bombeos",
    tanque: "tanques",
    colector: "colectores",
    vertido: "vertidos",
    poligono: "poligonos",
    subaglomeracion: "subaglomeracions",
    aglomeracion: "aglomeracions",
    edar: "edars",
    nucleo: "nucleos",
};

const getEntityUrl = (row) => {
    const type = row.associated_entity_type || row.entity_type;
    const id = row.associated_entity_id || row.entity_id;
    const concelloCode = row.concello_code;

    if (!type) return null;

    const normalizedType = type.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    if (normalizedType === "aglomeracion") {
        return id ? `/aglomeracions/${id}` : null;
    }

    if (normalizedType === "concello") {
        return id ? `/concellos/${id}` : null;
    }

    if (normalizedType === "conexion" || normalizedType === "conexions") {
        if (concelloCode) {
            return id
                ? `/concellos/${concelloCode}/saneam-conexions/info/conexions/${id}`
                : `/concellos/${concelloCode}/saneam-conexions`;
        }
        return null;
    }

    if (!id) return null;

    const plural = entityPathPluralMap[normalizedType];
    if (plural && concelloCode) {
        return `/concellos/${concelloCode}/info/${plural}/${id}`;
    }

    return null;
};


const ApprovalDashboard = () => {
    const navigate = useNavigate();
    const [elements, setElements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [query, updateQuery] = useListSearchParams(querySchema);
    const {page, sort, order} = query;
    const filter = useMemo(() => {
        const {page, sort, order, ...values} = query;
        return values;
    }, [query]);

    const [allEntities, setAllEntities] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [concellos, setConcellos] = useState([]);
    const [aglomeracions, setAglomeracions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [loadingConcellos, setLoadingConcellos] = useState(true);
    const [loadingAglomeracions, setLoadingAglomeracions] = useState(true);

    useEffect(() => {
        ConcelloService.getList()
            .then(data => {
                const list = data.results || data;
                if (Array.isArray(list)) {
                    list.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
                }
                setConcellos(list || []);
            })
            .catch(() => setConcellos([]))
            .finally(() => setLoadingConcellos(false));
        AglomeracionService.getList()
            .then(data => {
                const list = data.results || data;
                if (Array.isArray(list)) {
                    list.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
                }
                setAglomeracions(list || []);
            })
            .catch(() => setAglomeracions([]))
            .finally(() => setLoadingAglomeracions(false));
    }, []);

    const fetchOptions = useCallback(() => {
        setLoadingOptions(true);
        const apiFilter = {
            date_from: filter.date_from ? DateUtil.formatDateForAPI(filter.date_from) : "",
            date_to: filter.date_to ? DateUtil.formatDateForAPI(filter.date_to) : "",
            state: filter.state === "ALL" ? "" : filter.state,
            include_docs: true,
        };

        PendingChangesService.getList(apiFilter, null)
            .then(data => {
                const list = Array.isArray(data) ? data : (data?.results || []);
                const entities = list.map(el => el.entity_type).filter(Boolean);
                const users = list.map(el => el.action_user_label).filter(Boolean);
                setAllEntities(Array.from(new Set(entities)));
                setAllUsers(Array.from(new Set(users)));
                setLoadingOptions(false);
            })
            .catch(() => {
                setLoadingOptions(false);
            });
    }, [filter.date_from, filter.date_to, filter.state]);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    const uniqueEntities = useMemo(() => {
        const set = new Set(["", ...allEntities]);
        if (filter.entity) set.add(filter.entity);
        return Array.from(set);
    }, [allEntities, filter.entity]);

    const uniqueUsers = useMemo(() => {
        const set = new Set(["", ...allUsers]);
        if (filter.user) set.add(filter.user);
        return Array.from(set);
    }, [allUsers, filter.user]);

    const requestVersion = useRef(0);

    const fetchChanges = useCallback(() => {
        const request = ++requestVersion.current;
        setLoading(true);
        const apiFilter = {
            entity: filter.entity,
            user: filter.user,
            date_from: filter.date_from ? DateUtil.formatDateForAPI(filter.date_from) : "",
            date_to: filter.date_to ? DateUtil.formatDateForAPI(filter.date_to) : "",
            state: filter.state === "ALL" ? "" : filter.state,
            concello: filter.concello,
            aglomeracion: filter.aglomeracion,
            include_docs: true,
        };

        PendingChangesService.getList(apiFilter, page, sort, order)
            .then(data => {
                if (request !== requestVersion.current) return;
                setElements(data.results);
                setTotal(data.count);
                setLoading(false);
            })
            .catch(err => {
                if (request !== requestVersion.current) return;
                setError(err);
                setLoading(false);
            });
    }, [filter, page, sort, order]);

    useEffect(() => {
        fetchChanges();
        return () => {
            // Ignore responses from a page that has already been left.
            requestVersion.current += 1;
        };
    }, [fetchChanges]);

    const handleFilterChange = name => value => {
        updateQuery({[name]: value, page: 1});
    };

    const handleApprove = id => {
        PendingChangesService.approve(id)
            .then(() => {
                fetchChanges();
                fetchOptions();
            })
            .catch(err => setError(err));
    };

    const handleReject = id => {
        PendingChangesService.reject(id)
            .then(() => {
                fetchChanges();
                fetchOptions();
            })
            .catch(err => setError(err));
    };

    const handleRequestSort = (event, property) => {
        const isAsc = sort === property && order === "asc";
        updateQuery({sort: property, order: isAsc ? "desc" : "asc"});
    };

    const getStatusChip = status => {
        switch (status) {
            case "PENDING":
                return <Typography color="warning.main" variant="body2">Pendiente</Typography>;
            case "APPROVED":
                return <Typography color="success.main" variant="body2">Aprobado</Typography>;
            case "REJECTED":
                return <Typography color="error.main" variant="body2">Denegado</Typography>;
            default:
                return status;
        }
    };

    const tableColumns = [
        {id: "entity_type", label: "Entidad", width: 12},
        {id: "action_type", label: "Tipo Acción", width: 12},
        {id: "summary", label: "Resumen", width: 20, ordering: false},
        {id: "concello_label", label: "Concello", width: 12, ordering: false},
        {id: "aglomeracion_label", label: "Aglomeración", width: 12, ordering: false},
        {id: "action_user_label", label: "Usuario", width: 12, id_sort: "action_user__username"},
        {id: "action_date", label: "Fecha", width: 10},
        {id: "action_state", label: "Estado", width: 10},
    ];

    // Adapter for TableDownload because PendingChangesService.getList uses a different signature
    const downloadService = {
        getList: (filter, sort, order, format) => {
            const apiFilter = {
                entity: filter.entity,
                user: filter.user,
                date_from: filter.date_from ? DateUtil.formatDateForAPI(filter.date_from) : "",
                date_to: filter.date_to ? DateUtil.formatDateForAPI(filter.date_to) : "",
                state: filter.state === "ALL" ? "" : filter.state,
                concello: filter.concello,
                aglomeracion: filter.aglomeracion,
                include_docs: true,
            };
            // Pass null for page to avoid pagination when downloading
            return PendingChangesService.getList(apiFilter, null, sort, order, format);
        }
    };

    return (
        <PageLayout>
            <PaperContainer>
                <Typography variant="h5" sx={{mb: 3, fontWeight: "bold"}}>
                    Panel de Xestión de Cambios
                </Typography>

                <AlertError error={error} />

                <Stack sx={{width: "100%", bgcolor: "#ffffff", p: 1.5, mb: 4}}>
                    <Typography variant="h6" sx={{mb: 1}}>Filtro</Typography>
                    <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={6} md={3} lg={1.7}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Entidad</InputLabel>
                                <Select
                                    value={filter.entity}
                                    label="Entidad"
                                    onChange={e => handleFilterChange("entity")(e.target.value)}
                                    size="small"
                                    endAdornment={
                                        loadingOptions ? (
                                            <Box sx={{display: "flex", alignItems: "center", mr: 2, pointerEvents: "none"}}>
                                                <CircularProgress size={18} color="inherit" />
                                            </Box>
                                        ) : null
                                    }
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {loadingOptions ? (
                                        <MenuItem disabled value="" sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1.5}}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
                                        </MenuItem>
                                    ) : (
                                        uniqueEntities.filter(e => e !== "").map(entity => (
                                            <MenuItem key={entity} value={entity}>
                                                {entity}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.7}>
                            <Autocomplete
                                size="small"
                                options={uniqueUsers}
                                value={filter.user}
                                loading={loadingOptions}
                                loadingText={
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1}}>
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
                                    </Box>
                                }
                                onChange={(event, newValue) => {
                                    handleFilterChange("user")(newValue || "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Usuario"
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingOptions ? <CircularProgress color="inherit" size={18} sx={{mr: 1}} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                noOptionsText={
                                    loadingOptions ? (
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1}}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
                                        </Box>
                                    ) : (
                                        "No se encontraron usuarios"
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.8}>
                            <Autocomplete
                                size="small"
                                options={concellos}
                                value={concellos.find(option => String(option.codigo) === filter.concello) || null}
                                loading={loadingConcellos}
                                loadingText={
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1}}>
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
                                    </Box>
                                }
                                getOptionLabel={(option) => option.nome || ""}
                                onChange={(event, newValue) => {
                                    handleFilterChange("concello")(newValue ? String(newValue.codigo) : "");
                                }}
                                isOptionEqualToValue={(option, value) => option.codigo === value?.codigo}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Concello"
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingConcellos ? <CircularProgress color="inherit" size={18} sx={{mr: 1}} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                noOptionsText={
                                    loadingConcellos ? (
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1}}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
                                        </Box>
                                    ) : (
                                        "Non se atoparon concellos"
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Autocomplete
                                size="small"
                                options={aglomeracions}
                                value={aglomeracions.find(option => String(option.id) === filter.aglomeracion) || null}
                                loading={loadingAglomeracions}
                                loadingText={
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1}}>
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
                                    </Box>
                                }
                                getOptionLabel={(option) => option.nome || ""}
                                onChange={(event, newValue) => {
                                    handleFilterChange("aglomeracion")(newValue ? String(newValue.id) : "");
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Aglomeración"
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingAglomeracions ? <CircularProgress color="inherit" size={18} sx={{mr: 1}} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                noOptionsText={
                                    loadingAglomeracions ? (
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1}}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">Recuperando datos...</Typography>
                                        </Box>
                                    ) : (
                                        "Non se atoparon aglomeracións"
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    value={filter.state}
                                    label="Estado"
                                    onChange={e => handleFilterChange("state")(e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="ALL">Todos</MenuItem>
                                    <MenuItem value="PENDING">Pendientes</MenuItem>
                                    <MenuItem value="APPROVED">Aprobados</MenuItem>
                                    <MenuItem value="REJECTED">Denegados</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.6}>
                            <DatePicker
                                label="Desde"
                                value={filter.date_from}
                                onChange={handleFilterChange("date_from")}
                                renderInput={params => <TextField {...params} size="small" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.6}>
                            <DatePicker
                                label="Hasta"
                                value={filter.date_to}
                                onChange={handleFilterChange("date_to")}
                                renderInput={params => <TextField {...params} size="small" fullWidth />}
                            />
                        </Grid>
                    </Grid>
                </Stack>

                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", my: 5}}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{border: "1px solid #eee"}}>
                        <Table sx={{minWidth: 650}} aria-label="tabla de cambios" size="small">
                            <EntityTableSortingHead
                                order={order}
                                attribute={sort}
                                onRequestSort={handleRequestSort}
                                headCells={tableColumns.map(col => ({
                                    ...col,
                                    id: col.id_sort || col.id
                                }))}
                                actionsHeadcell={true}
                            />
                            <TableBody>
                                {elements.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{py: 3}}>
                                            No se encontraron cambios.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    elements.map(row => {
                                        const url = getEntityUrl(row);
                                        return (
                                            <TableRow 
                                                key={row.id} 
                                                hover
                                                onClick={(e) => {
                                                    if (e.target.closest("button") || e.target.closest(".MuiButton-root")) {
                                                        return;
                                                    }
                                                    if (url) {
                                                        navigate(url);
                                                    }
                                                }}
                                                sx={{cursor: url ? "pointer" : "default"}}
                                            >
                                                <TableCell sx={{fontSize: "0.8rem"}}>{row.entity_type}</TableCell>
                                                <TableCell sx={{fontSize: "0.8rem"}}>{row.action_type}</TableCell>
                                                <TableCell sx={{fontSize: "0.8rem"}}>{row.summary}</TableCell>
                                                <TableCell sx={{fontSize: "0.8rem"}}>{row.concello_label || "-"}</TableCell>
                                                <TableCell sx={{fontSize: "0.8rem"}}>{row.aglomeracion_label || "-"}</TableCell>
                                                <TableCell sx={{fontSize: "0.8rem"}}>{row.action_user_label}</TableCell>
                                                <TableCell sx={{fontSize: "0.8rem"}}>
                                                    {DateUtil.formatDateTime(row.action_date)}
                                                </TableCell>
                                                <TableCell sx={{fontSize: "0.8rem"}}>{getStatusChip(row.action_state)}</TableCell>
                                            <TableCell sx={{fontSize: "0.8rem"}}>
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    {row.action_state === "PENDING" ? (
                                                        <>
                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                size="small"
                                                                startIcon={<ThumbUpIcon />}
                                                                onClick={() => handleApprove(row.id)}
                                                                sx={{fontSize: "0.7rem"}}
                                                            >
                                                                Aprobar
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                size="small"
                                                                startIcon={<ThumbDownIcon />}
                                                                onClick={() => handleReject(row.id)}
                                                                sx={{fontSize: "0.7rem"}}
                                                            >
                                                                Denegar
                                                            </Button>
                                                        </>
                                                    ) : row.action_state === "APPROVED" ? (
                                                        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", color: "success.main"}}>
                                                            <Box sx={{display: "flex", alignItems: "center"}}>
                                                                <CheckCircleIcon sx={{mr: 0.5}} fontSize="small" />
                                                                <Typography variant="caption" sx={{fontWeight: "bold"}}>Aprobado</Typography>
                                                            </Box>
                                                            {row.resolution_user_label && (
                                                                <Typography variant="caption" color="text.secondary" sx={{fontSize: "0.65rem"}}>
                                                                    Por: {row.resolution_user_label}
                                                                </Typography>
                                                            )}
                                                            {row.resolution_date && (
                                                                <Typography variant="caption" color="text.secondary" sx={{fontSize: "0.65rem"}}>
                                                                    {DateUtil.formatDateTime(row.resolution_date)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", color: "error.main"}}>
                                                            <Box sx={{display: "flex", alignItems: "center"}}>
                                                                <CancelIcon sx={{mr: 0.5}} fontSize="small" />
                                                                <Typography variant="caption" sx={{fontWeight: "bold"}}>Denegado</Typography>
                                                            </Box>
                                                            {row.resolution_user_label && (
                                                                <Typography variant="caption" color="text.secondary" sx={{fontSize: "0.65rem"}}>
                                                                    Por: {row.resolution_user_label}
                                                                </Typography>
                                                            )}
                                                            {row.resolution_date && (
                                                                <Typography variant="caption" color="text.secondary" sx={{fontSize: "0.65rem"}}>
                                                                    {DateUtil.formatDateTime(row.resolution_date)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Grid container justifyContent="space-between" alignItems="center" sx={{mt: 3}}>
                    <Grid item>
                        <TableDownload
                            service={downloadService}
                            filter={filter}
                            sort={sort}
                            order={order}
                            formats={[ServiceRequestFormat.CSV]}
                        />
                    </Grid>
                    <Grid item>
                        <Pagination
                            count={Math.ceil(total / pageSize)}
                            page={page}
                            onChange={(e, v) => updateQuery({page: v})}
                            color="primary"
                        />
                    </Grid>
                </Grid>
            </PaperContainer>
        </PageLayout>
    );
};

export default ApprovalDashboard;
